import React, { useState } from 'react';
import { ChatBubbleLeftIcon, DocumentIcon, PlusIcon } from '@heroicons/react/24/outline';
import Card from '../components/Card';
import quoteService from '../../../services/quote.service';
import type { QuoteNoteRow } from '../fixtures';

const HEADERS = ['Date / Time', 'User', 'Rev', 'Note'];

const _esc = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

/** Build a print-friendly notes document (V1 index_quote.pl:513-592 printable
 *  window). Notes arrive newest-first from the API; that order is preserved. */
export function buildNotesPrintHtml(notes: QuoteNoteRow[], title = 'Quote Notes'): string {
  const rows = notes.map((n) => (
    `<tr><td>${_esc(n.datetime || '')}</td><td>${_esc(n.user || '')}</td>`
    + `<td>${n.revisionNumber}</td><td>${_esc(n.body || '')}</td></tr>`
  )).join('');
  return `<!doctype html><html><head><meta charset="utf-8"><title>${_esc(title)}</title>`
    + `<style>body{font-family:Arial,Helvetica,sans-serif;font-size:12px;margin:24px;}`
    + `h2{font-size:15px;}table{border-collapse:collapse;width:100%;}`
    + `th,td{border:1px solid #ccc;padding:4px 7px;text-align:left;vertical-align:top;}`
    + `th{background:#eee;}td:nth-child(4){white-space:pre-wrap;}</style></head><body>`
    + `<h2>${_esc(title)}</h2>`
    + `<table><thead><tr><th>Date / Time</th><th>User</th><th>Rev</th><th>Note</th></tr></thead>`
    + `<tbody>${rows}</tbody></table></body></html>`;
}

interface Props {
  notes: QuoteNoteRow[];
  /** When set (saved quote + write access), an add-note composer is shown.
   *  `onAdded` is called after a successful POST so the page can re-fetch. */
  quoteNumber?: number;
  revisionNumber?: number;
  canAdd?: boolean;
  onAdded?: () => void;
}

const NotesTab: React.FC<Props> = ({ notes, quoteNumber, revisionNumber, canAdd, onAdded }) => {
  const [draft, setDraft] = useState('');
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const composerEnabled = Boolean(canAdd && quoteNumber && revisionNumber != null);

  const printNotes = () => {
    const w = window.open('', '_blank');
    if (!w) return;  // popup blocked
    w.document.write(buildNotesPrintHtml(notes, quoteNumber ? `Quote ${quoteNumber} — Notes` : 'Quote Notes'));
    w.document.close();
    w.focus();
    w.print();
  };

  const submit = async () => {
    const body = draft.trim();
    if (!body || posting || !quoteNumber || revisionNumber == null) return;
    setPosting(true);
    setError(null);
    try {
      await quoteService.addNote(quoteNumber, revisionNumber, body);
      setDraft('');
      onAdded?.();
    } catch {
      setError('Failed to add the note. Please try again.');
    } finally {
      setPosting(false);
    }
  };

  return (
    <Card
      icon={<ChatBubbleLeftIcon className="h-3.5 w-3.5" />}
      title="Notes"
      right={
        <div className="flex items-center gap-3">
          <span className="text-[0.6875rem] text-slate-500 dark:text-slate-400">{notes.length} {notes.length === 1 ? 'note' : 'notes'}</span>
          <button type="button" onClick={printNotes} disabled={notes.length === 0} className="px-2.5 py-1 text-[0.71875rem] font-medium text-slate-700 bg-white border border-slate-300 rounded cursor-pointer inline-flex items-center gap-1.5 hover:bg-slate-50 disabled:opacity-40 disabled:cursor-default dark:text-slate-200 dark:bg-slate-700 dark:border-slate-600 dark:hover:bg-slate-600">
            <DocumentIcon className="h-3.5 w-3.5" />
            <span><u>P</u>rintable Version</span>
          </button>
        </div>
      }
      flushBody
    >
      {composerEnabled && (
        <div className="px-3 py-2.5 bg-blue-50/60 border-b border-blue-100 dark:bg-blue-900/20 dark:border-blue-800">
          <div className="flex items-start gap-2">
            <textarea
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="Add a note… (saved in UPPERCASE, stamped with your user and the current time)"
              aria-label="New note"
              className="flex-1 min-h-[54px] border border-slate-300 rounded px-2 py-1.5 text-[0.75rem] text-slate-900 bg-white resize-y font-sans dark:border-slate-600 dark:text-slate-100 dark:bg-slate-900"
            />
            <button
              type="button"
              onClick={submit}
              disabled={!draft.trim() || posting}
              className="px-3 py-1.5 text-[0.71875rem] font-semibold text-white bg-blue-600 border border-blue-700 rounded inline-flex items-center gap-1 disabled:opacity-40 hover:bg-blue-700"
            >
              <PlusIcon className="h-3 w-3" /> {posting ? 'Adding…' : 'Add Note'}
            </button>
          </div>
          {error && <div className="mt-1 text-[0.71875rem] text-red-600 dark:text-red-400">{error}</div>}
        </div>
      )}

      <div className="grid grid-cols-[150px_140px_60px_1fr] bg-slate-100 border-b border-slate-200 dark:bg-slate-900/40 dark:border-slate-700">
        {HEADERS.map((h, i) => (
          <div
            key={h}
            className={`px-3 py-2 text-[0.65625rem] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider ${i < HEADERS.length - 1 ? 'border-r border-slate-200 dark:border-slate-700' : ''}`}
          >
            {h}
          </div>
        ))}
      </div>

      {notes.length === 0 ? (
        <div className="px-4 py-10 text-center text-slate-400 dark:text-slate-500 text-[0.78125rem] bg-white dark:bg-slate-800">
          No notes on this quote yet.
        </div>
      ) : (
        notes.map((n, i) => (
          <div
            key={n.noteId}
            className={`grid grid-cols-[150px_140px_60px_1fr] items-stretch ${i < notes.length - 1 ? 'border-b border-slate-100 dark:border-slate-700' : ''}`}
          >
            <div className="px-3 py-2.5 bg-slate-50/60 dark:bg-slate-900/30 border-r border-slate-100 dark:border-slate-700 text-[0.71875rem] text-slate-600 dark:text-slate-300">
              {n.datetime || '—'}
            </div>
            <div className="px-3 py-2.5 bg-slate-50/60 dark:bg-slate-900/30 border-r border-slate-100 dark:border-slate-700 text-[0.75rem] text-slate-700 dark:text-slate-300 flex items-start gap-1.5">
              <div className="w-[22px] h-[22px] rounded-full bg-gradient-to-br from-blue-600 to-purple-600 text-white text-[0.625rem] font-bold inline-flex items-center justify-center flex-shrink-0">
                {(n.user || '?').slice(0, 2).toUpperCase()}
              </div>
              <div className="font-semibold text-slate-900 dark:text-slate-100 min-w-0 overflow-hidden text-ellipsis">{n.user || '—'}</div>
            </div>
            <div className="px-3 py-2.5 bg-slate-50/60 dark:bg-slate-900/30 border-r border-slate-100 dark:border-slate-700 text-[0.75rem] text-slate-500 dark:text-slate-400 font-semibold">
              {n.revisionNumber}
            </div>
            <div className="p-1.5">
              <div className="px-2.5 py-2 text-[0.75rem] text-slate-900 dark:text-slate-100 whitespace-pre-wrap leading-snug">
                {n.body || <span className="text-slate-300 dark:text-slate-600 italic">(empty)</span>}
              </div>
            </div>
          </div>
        ))
      )}
    </Card>
  );
};

export default NotesTab;
